/**
 * Plan 34 slice 2 — the client half of the WORLD RECORD board.
 *
 * Anonymous identity (`jreb1d.player.v1`, localStorage), a typed client for the two Pages
 * Functions endpoints, a 60 s sessionStorage board cache and a per-run submit guard.
 * Every storage/fetch op is injectable so the rules are unit-tested without a browser, and
 * NOTHING here throws: an unreachable API (or a spent free-tier quota) is the value
 * `{ ok: false }`, never an exception in the end screen.
 *
 * Contract: [plans/34-leaderboard-cloudflare.md](../../plans/34-leaderboard-cloudflare.md).
 */

import type { StorageLike } from './levelStore';

export const PLAYER_KEY = 'jreb1d.player.v1';
export const BOARD_KEY = 'jreb1d.board.v1';
export const BOARD_TTL_MS = 60_000;
export const TOP_DEFAULT = 20;
export const TOP_CAP = 50;
export const NAME_MIN = 3;
export const NAME_MAX = 16;
export const SCORE_MAX = 250_000;
export const DEPTH_MAX = 999_999;

export const OUTCOMES = ['Squad lost', 'Operation secured'] as const;
export type Outcome = (typeof OUTCOMES)[number];

/** The one failure copy the end screen shows for every failure class (offline, 503, 429, 400). */
export const FAILURE_COPY = 'World record unavailable — records stay in this browser.';

export interface Player {
  /** Anonymous client id — a UUID v4, the only identity this player ever has. */
  id: string;
  /** The callsign they chose last time (absent until the first submission). */
  name?: string;
}

export interface PlayerStore {
  /** Always a player: reads storage, mints an id on first use, memory after the first throw. */
  read(): Player;
  remember(player: Player): void;
  /** False once ANY storage op has thrown (sticky), same contract as `levelStore`. */
  readonly persistent: boolean;
}

export interface BoardEntry {
  rank: number;
  name: string;
  score: number;
  depth: number;
  outcome: string;
  createdAt: number;
}

export interface Board {
  total: number;
  top: BoardEntry[];
}

export interface ScoreEntry {
  clientId: string;
  name: string;
  score: number;
  depth: number;
  outcome: Outcome;
}

export type SubmitResult =
  | { ok: true; rank: number; total: number; board: Board }
  | { ok: false };

/** The slice of `fetch` this module uses — narrow enough for honest test fakes. */
export interface FetchLike {
  (url: string, init: { method: string; headers?: Record<string, string>; body?: string }): Promise<{
    ok: boolean;
    json(): Promise<unknown>;
  }>;
}

export interface LeaderboardClient {
  board(limit?: number): Promise<Board | null>;
  submit(entry: ScoreEntry): Promise<SubmitResult>;
}

export interface SubmitGuard {
  /** True exactly once per run key: the affordance can never submit a run twice. */
  claim(runKey: string): boolean;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
/** Mirror of the API's name clamp, so the input enables LOG only for something the server keeps. */
const NAME_STRIP_RE = /[^A-Za-z0-9 .\-']/g;

function isInt(value: unknown, max: number): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= max;
}

export function clientName(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const collapsed = raw.replace(NAME_STRIP_RE, '').replace(/ +/g, ' ').trim();
  if (collapsed.length < NAME_MIN || collapsed.length > NAME_MAX) return null;
  return collapsed;
}

/**
 * `crypto.randomUUID()` needs a secure context, so a LAN `pnpm dev --host` tab (plain http)
 * falls back to a hand-rolled v4 from `getRandomValues` — same shape the API validates.
 */
export function newClientId(): string {
  const c = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();
  const bytes = c.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const hex = [...bytes].map(b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export function parsePlayer(raw: string | null): Player | null {
  if (!raw || raw.length > 400) return null;
  try {
    const p = JSON.parse(raw) as Player;
    if (!p || typeof p.id !== 'string' || !UUID_RE.test(p.id)) return null;
    const name = clientName(p.name);
    return name ? { id: p.id.toLowerCase(), name } : { id: p.id.toLowerCase() };
  } catch {
    return null;
  }
}

export function createPlayerStore(get: () => StorageLike = () => localStorage): PlayerStore {
  // Per-instance memory: the id survives the storage throw, so a private-mode player still
  // submits with a stable identity inside this tab.
  let memory: Player | null = null;
  let persistent = true;

  const mint = (): Player => (memory = { id: newClientId() });

  const remember = (player: Player) => {
    const next = parsePlayer(JSON.stringify(player)) ?? { id: player.id.toLowerCase() };
    memory = next;
    if (!persistent) return;
    try {
      get().setItem(PLAYER_KEY, JSON.stringify(next));
    } catch {
      persistent = false;
    }
  };

  return {
    get persistent() {
      return persistent;
    },
    read(): Player {
      if (!persistent) return memory ?? mint();
      try {
        const stored = parsePlayer(get().getItem(PLAYER_KEY));
        if (stored) {
          memory = stored;
          return stored;
        }
        const player = memory ?? mint();
        remember(player); // the minted id is written straight away: it IS the identity
        return player;
    } catch {
      persistent = false;
      return memory ?? mint();
    }
  },
    remember,
  };
}

/** Runs are identified by seed + depth + end state: a restart is a new run and may submit again. */
export function runKey(run: { seed: string; depth: number; status: string }): string {
  return `${run.seed}:${run.depth}:${run.status}`;
}

export function createSubmitGuard(): SubmitGuard {
  const seen = new Set<string>();
  return {
    claim(key: string) {
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    },
  };
}

export function rankLine(rank: number, total: number): string {
  const n = Math.max(0, Math.trunc(total)).toLocaleString('en-US');
  return `RANK #${Math.max(1, Math.trunc(rank))} · ${n} OPERATIONS LOGGED`;
}

export function normalizeLimit(raw: number | undefined): number {
  if (!Number.isFinite(raw)) return TOP_DEFAULT;
  return Math.min(TOP_CAP, Math.max(1, Math.trunc(raw as number)));
}

function parseBoard(data: unknown): Board | null {
  const d = data as { ok?: boolean; total?: unknown; top?: unknown };
  if (!d || d.ok !== true || !isInt(d.total, 1e9) || !Array.isArray(d.top)) return null;
  const top = d.top.filter((e: unknown): e is BoardEntry => {
    const row = e as BoardEntry;
    return !!row && isInt(row.rank, 1e9) && typeof row.name === 'string' && isInt(row.score, SCORE_MAX);
  });
  return { total: d.total, top };
}

function parseCache(raw: string | null, nowMs: number): Board | null {
  if (!raw || raw.length > 8000) return null;
  try {
    const cached = JSON.parse(raw) as { at: number; board: Board };
    if (!isInt(cached.at, 1e15) || nowMs - cached.at >= BOARD_TTL_MS || nowMs - cached.at < 0) return null;
    const board = parseBoard({ ok: true, ...cached.board });
    return board;
  } catch {
    return null;
  }
}

/**
 * One fetch path, one cache layer. `board()` serves the 60 s cache first, then the network;
 * a failed board is NOT cached so the next call retries instead of pinning an outage.
 */
export function createLeaderboardClient(
  options: { fetchImpl?: FetchLike; cache?: StorageLike | null; now?: () => number } = {},
): LeaderboardClient {
  const now = options.now ?? Date.now;
  const call: FetchLike = options.fetchImpl ?? ((url, init) => fetch(url, init));
  // Lazily touched: the getter itself throws in some privacy modes, so creation stays clean.
  const cache = options.cache === undefined
    ? () => { try { return sessionStorage; } catch { return null; } }
    : () => options.cache ?? null;

  const readCache = (): Board | null => {
    const store = cache();
    if (!store) return null;
    try {
      return parseCache(store.getItem(BOARD_KEY), now());
    } catch {
      return null;
    }
  };

  const writeCache = (board: Board) => {
    const store = cache();
    if (!store) return;
    try {
      store.setItem(BOARD_KEY, JSON.stringify({ at: now(), board }));
    } catch {
      // A full/unavailable sessionStorage only costs a fetch later — never the run.
    }
  };

  return {
    async board(limit) {
      const cached = readCache();
      if (cached) return cached;
      try {
        const res = await call(`/api/leaderboard?limit=${normalizeLimit(limit)}`, { method: 'GET' });
        const board = res.ok ? parseBoard(await res.json().catch(() => null)) : null;
        if (board) {
          writeCache(board);
          return board;
        }
      } catch {
        return null;
      }
      return null;
    },

    async submit(entry) {
      const name = clientName(entry.name);
      if (!name || !isInt(entry.score, SCORE_MAX) || !isInt(entry.depth, DEPTH_MAX) || entry.depth < 1) {
        return { ok: false };
      }
      try {
        const res = await call('/api/score', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...entry, name }),
        });
        if (!res.ok) return { ok: false };
        const data = await res.json().catch(() => null);
        const d = data as { ok?: boolean; rank?: unknown; total?: unknown; top?: unknown } | null;
        if (!d || d.ok !== true || !isInt(d.rank, 1e9) || !isInt(d.total, 1e9)) return { ok: false };
        const board = parseBoard({ ok: true, total: d.total, top: d.top });
        if (!board) return { ok: false };
        writeCache(board); // the payload carries the fresh board: no second fetch
        return { ok: true, rank: d.rank, total: d.total, board };
      } catch {
        return { ok: false };
      }
    },
  };
}
